/* eslint-disable react-hooks/exhaustive-deps */

// Likely you don't want to import this file directly.
// Instead prefer:
//   import {
//     profilePersonalInfoPage,
//     profileContactInfoPages,
//   } from 'platform/forms-system/src/js/patterns/prefill';

import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';
import { Element, scrollTo, scrollAndFocus } from 'platform/utilities/scroll';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import {
  selectProfile,
  isLoggedIn,
} from '@department-of-veterans-affairs/platform-user/selectors';
import { generateMockUser } from 'platform/site-wide/user-nav/tests/mocks/user';
import { isMinimalHeaderPath } from 'platform/forms-system/src/js/patterns/minimal-header';
import AddressView from 'platform/user/profile/vap-svc/components/AddressField/AddressView';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import {
  getReturnState,
  clearReturnState,
  markAddressSavedByUser,
  wasAddressSavedByUser,
  clearAddressSavedByUser,
  renderTelephone,
  getMissingInfo,
  REVIEW_CONTACT,
  convertNullishObjectValuesToEmptyString,
  contactInfoPropTypes,
} from 'platform/forms-system/src/js/utilities/data/profile';
import { getValidationErrors } from 'platform/forms-system/src/js/utilities/validations';

import {
  isFieldEmpty,
  areAddressesEqual,
} from 'platform/user/profile/vap-svc/util';
import { FIELD_NAMES } from 'platform/user/profile/vap-svc/constants';
import { openReviewChapter } from 'platform/forms-system/src/js/actions';
import { isNavButtonAnalyticsEnabled } from 'platform/forms-system/src/js/helpers';
import ContactInfoCard from './ContactInfoCard';
import ProfileAddressUpdatedAlert from './ProfileAddressUpdatedAlert';

/**
 * Render contact info page
 * @param {Object} data - full form data
 * @param {Function} goBack - CustomPage param
 * @param {Function} goForward - CustomPage param
 * @param {Boolean} onReviewPage - CustomPage param
 * @param {String[]} order - optional list that controls the render order of contact sections
 * @param {Element} contentBeforeButtons - CustomPage param
 * @param {Element} contentAfterButtons - CustomPage param
 * @param {Function} setFormData - CustomPage param
 * @param {Object} content - Contact info page content
 * @param {String} contactPath - Contact info path; used in edit page path
 * @parma {import('../utilities/data/profile').ContactInfoKeys} keys - contact info data key
 * @param {String[]} requiredKeys - list of keys of required fields
 * @returns
 */
export const ContactInfoBase = ({
  data,
  goBack,
  goForward,
  onReviewPage,
  chapterKey,
  contentBeforeButtons,
  contentAfterButtons,
  setFormData,
  content,
  contactInfoPageDescription,
  contactInfoPageTitle,
  keys,
  requiredKeys,
  schema,
  uiSchema,
  testContinueAlert = false,
  contactInfoPageKey,
  disableMockContactInfo = false,
  contactSectionHeadingLevel,
  mainHeaderLevel,
  contactPath,
  order,
  showReviewPageRequiredFields = false,
  usePageSchemaForRequiredFields = false,
  useWebComponentNavButtons = false,
  ...rest
}) => {
  const { router, formContext, formOptions } = rest;

  let urlPrefix = '';

  if (router && router?.routes && router?.routes?.length > 0) {
    urlPrefix = router?.routes?.[1]?.formConfig?.urlPrefix || '';
  }

  const baseEditPath = `${urlPrefix}${contactPath}`;
  const MISSING_ALERT_TEXT = 'Missing';
  const wrapRef = useRef(null);
  // this is the ref that gets focus
  const errorCardRef = useRef(null);
  const addressErrorCardRef = useRef(null);
  const emailErrorCardRef = useRef(null);
  const homePhoneErrorCardRef = useRef(null);
  const mobilePhoneErrorCardRef = useRef(null);

  window.sessionStorage.setItem(REVIEW_CONTACT, onReviewPage || false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hadError, setHadError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editState] = useState(getReturnState());
  const [addressSavedEarlier, setAddressSavedEarlier] = useState(
    wasAddressSavedByUser(),
  );
  const hasAttemptedSubmit =
    useSelector(state => state.form?.submission?.hasAttemptedSubmit) || false;

  // vapContactInfo is an empty object locally, so mock it
  const profile = useSelector(selectProfile) || {};
  const loggedIn = useSelector(isLoggedIn) || false;
  const contactInfo =
    loggedIn && environment.isLocalhost() && !disableMockContactInfo
      ? generateMockUser({ authBroker: 'iam' }).data.attributes
          .vet360ContactInformation
      : profile.vapContactInfo || {};

  const dataWrap = data[keys.wrapper] || {};

  // Review & Submit only: announce when the on-mount profile sync has
  // replaced the mailing address the user last saved with a newer profile
  // address. The working copy now matches the profile, but the
  // save-in-progress payload (form.loadedData, never rewritten after the form
  // loads) still holds what the user last saw — comparing the two is what
  // detects the swap, with no extra network request.
  const dispatch = useDispatch();
  const savedFormAddress = useSelector(
    state => state.form?.loadedData?.formData?.[keys.wrapper]?.[keys.address],
  );
  const workingAddress = dataWrap[keys.address];
  // that stale baseline also makes the user's own address edit look like a
  // sync, so track who put the current address there: this mount is a return
  // from a save on the address edit page, or an earlier save marked the
  // session. A canceled edit changed nothing, so it doesn't count.
  const addressJustSaved = editState === 'address,updated';
  const addressSavedByUser = addressJustSaved || addressSavedEarlier;
  const savedAddressMatchesForm =
    !!savedFormAddress?.addressLine1 &&
    !!workingAddress?.addressLine1 &&
    areAddressesEqual(savedFormAddress, workingAddress);
  const addressUpdatedToMatchProfile =
    !!onReviewPage &&
    loggedIn &&
    !addressSavedByUser &&
    !!savedFormAddress?.addressLine1 &&
    !!workingAddress?.addressLine1 &&
    !savedAddressMatchesForm &&
    // the difference came from the sync — a form-only edit leaves the
    // working copy differing from the profile, and stays silent
    areAddressesEqual(workingAddress, contactInfo?.mailingAddress);
  useEffect(() => {
    // expand the chapter so the announcement is actually visible
    if (addressUpdatedToMatchProfile && chapterKey) {
      dispatch(openReviewChapter(chapterKey));
    }
  }, [addressUpdatedToMatchProfile, chapterKey, dispatch]);
  useEffect(() => {
    if (addressJustSaved) {
      // outlives editState (cleared a second from now) so later visits to the
      // review page don't mistake this edit for a sync
      markAddressSavedByUser();
    } else if (savedAddressMatchesForm) {
      // loadedData has caught up with the working copy, so the marker is spent
      clearAddressSavedByUser();
      setAddressSavedEarlier(false);
    }
  }, [addressJustSaved, savedAddressMatchesForm]);

  const configuredRequiredKeys = usePageSchemaForRequiredFields
    ? schema?.properties?.[keys.wrapper]?.required || requiredKeys || []
    : requiredKeys || [];
  // Review & Submit keeps its existing summary behavior unless a form opts in
  // to showing the same Required/Missing card indicators as the main page.
  const effectiveRequiredKeys =
    onReviewPage &&
    usePageSchemaForRequiredFields &&
    !showReviewPageRequiredFields
      ? []
      : configuredRequiredKeys;
  const showCardErrors =
    submitted ||
    (onReviewPage && showReviewPageRequiredFields && hasAttemptedSubmit);
  const isCardRequired = (fieldName, fieldKey) =>
    effectiveRequiredKeys.includes(
      usePageSchemaForRequiredFields ? fieldKey : fieldName,
    );

  const missingInfo = getMissingInfo({
    data: dataWrap,
    keys,
    content,
    requiredKeys: effectiveRequiredKeys,
  });

  const validationErrors = uiSchema?.['ui:required']?.(data)
    ? getValidationErrors(uiSchema?.['ui:validations'] || [], {}, data)
    : [];

  // mostRecentlySavedField set whenever a profile update is successful.
  const { mostRecentlySavedField } =
    useSelector(state => state.vapService) || {};

  // Unified field configuration mapping
  const fieldConfig = {
    address: {
      id: 'address',
      fieldName: FIELD_NAMES.MAILING_ADDRESS,
      key: keys.address,
      path: 'mailingAddress',
      text: content.mailingAddress,
    },
    'home-phone': {
      id: 'home-phone',
      fieldName: FIELD_NAMES.HOME_PHONE,
      key: keys.homePhone,
      path: 'homePhone',
      text: content.homePhone,
    },
    'mobile-phone': {
      id: 'mobile-phone',
      fieldName: FIELD_NAMES.MOBILE_PHONE,
      key: keys.mobilePhone,
      path: 'mobilePhone',
      text: content.mobilePhone,
    },
    email: {
      id: 'email',
      fieldName: FIELD_NAMES.EMAIL,
      key: keys.email,
      path: 'email',
      text: content.email,
    },
  };

  //   editState  = "address,updated"  (from sessionStorage)
  //   editField  = "address"          (the fieldConfig id, before the comma)
  //   fieldName  = "mailingAddress"   (the FIELD_NAMES key vap-svc uses)
  const [editField] = editState?.split(',') || [];
  const fieldName = fieldConfig[editField]?.fieldName;
  const hasProfileUpdate = !!fieldName && mostRecentlySavedField === fieldName;

  const handlers = {
    onSubmit: event => {
      // This prevents this nested form submit event from passing to the
      // outer form and causing a page advance
      event.stopPropagation();
    },
    onGoBack: () => {
      clearReturnState();
      goBack();
    },
    onGoForward: () => {
      setSubmitted(true);
      if (missingInfo.length) {
        scrollAndFocus(errorCardRef.current?.current);
      } else if (validationErrors.length) {
        scrollAndFocus(wrapRef.current);
      } else {
        clearReturnState();
        goForward(data);
      }
    },
  };

  const lastSyncedData = useRef({});

  const isFormOnlyUpdate = formValue =>
    formValue?.updateProfileChoice === 'no' ||
    formValue?.formOnlyUpdate === true;

  const syncProfileData = () => {
    const wrapper = { ...data[keys.wrapper] };
    const updatedWrapper = { ...wrapper };
    let needsUpdate = false;

    Object.values(fieldConfig).forEach(({ key, path }) => {
      const profileValue = contactInfo?.[path];
      const formValue = wrapper?.[key];

      if (profileValue === null) {
        // profile is (now) null: clear this field in the form
        // unless the user saved it to this form only
        if (
          JSON.stringify(lastSyncedData.current[key]) !== 'null' &&
          !isFormOnlyUpdate(formValue)
        ) {
          updatedWrapper[key] = path === 'email' ? { emailAddress: '' } : {};
          lastSyncedData.current[key] = null;
          needsUpdate = true;
        }

        // field absent from profile, skip to next
        return;
      }

      // Prefer the more recent value between profile and form
      const profileUpdated = profileValue?.updatedAt || '';
      const formUpdated = formValue?.updatedAt || '';
      const isFormNewer = formUpdated && formUpdated > profileUpdated;
      const selectedValue = isFormNewer ? formValue : profileValue;

      if (
        key &&
        selectedValue &&
        JSON.stringify(lastSyncedData.current[key]) !==
          JSON.stringify(selectedValue)
      ) {
        const cleanedValue =
          path === 'email'
            ? {
                ...selectedValue,
                emailAddress: selectedValue?.emailAddress || '',
              }
            : convertNullishObjectValuesToEmptyString(selectedValue);

        updatedWrapper[key] = cleanedValue;
        lastSyncedData.current[key] = selectedValue;
        needsUpdate = true;

        if (
          key === keys.address &&
          !isFormNewer &&
          !areAddressesEqual(formValue, profileValue) &&
          // the edit page only writes the form copy for form-only saves, so
          // this same replace is how the user's own profile save reaches the
          // form on the return mount - that one is theirs, not a clobber
          !addressJustSaved
        ) {
          // the sync just overrode the address the user put here, so the
          // review page should announce it like any other sync
          clearAddressSavedByUser();
          setAddressSavedEarlier(false);
        }
      }
    });

    if (needsUpdate) {
      setFormData({ ...data, [keys.wrapper]: updatedWrapper });
    }
  };

  useEffect(() => syncProfileData(), [contactInfo, data, keys, setFormData]);

  useEffect(() => {
    if (editState) {
      const [lastEdited, returnState] = editState.split(',');
      const scrollTimer = setTimeout(() => {
        const target =
          returnState === 'canceled'
            ? `#edit-${lastEdited}`
            : `#updated-${lastEdited}`;
        scrollTo(
          onReviewPage
            ? `${contactInfoPageKey}ScrollElement`
            : `header-${lastEdited}`,
        );
        focusElement(onReviewPage ? `#${contactInfoPageKey}Header` : target);
      });

      const clearTimer = setTimeout(() => {
        clearReturnState();
      }, 1000);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(clearTimer);
      };
    }
    focusElement(`#${contactInfoPageKey}Header`);

    return undefined;
  }, [contactInfoPageKey, editState, onReviewPage]);

  useEffect(() => {
    if ((hasInitialized && missingInfo.length) || testContinueAlert) {
      // page had an error flag, so we know when to show a success alert
      setHadError(true);
    }
    const timer = setTimeout(() => {
      setHasInitialized(true);
    });

    return () => clearTimeout(timer);
  }, [missingInfo, hasInitialized, testContinueAlert]);

  const isMinimalHeader = isMinimalHeaderPath();
  const isFlatReview = Boolean(formContext?.flatReview);

  const baseHeaderLevel = parseInt(
    (mainHeaderLevel || 'h3').replace('h', ''),
    10,
  );
  // Calculates the appropriate header level for subheadings based on the main header level and an offset, ensuring it does not exceed h6.
  const offsetLevel = offset => Math.min(baseHeaderLevel + offset, 6);

  let MainHeader = mainHeaderLevel || 'h3';
  if (onReviewPage && isFlatReview) {
    MainHeader = 'h3';
  } else if (onReviewPage) {
    MainHeader = `h${offsetLevel(1)}`;
  } else if (isMinimalHeader) {
    MainHeader = 'h1';
  }

  let mainHeaderClass =
    isMinimalHeader && !onReviewPage
      ? 'vads-u-margin-top--3 vads-u-margin-bottom--0 vads-u-font-size--h2'
      : 'vads-u-margin-top--3 vads-u-margin-bottom--0';
  if (onReviewPage && isFlatReview) {
    mainHeaderClass += ' vads-u-font-size--h4';
  }

  let headerLevel = contactSectionHeadingLevel;
  if (!headerLevel) {
    if (isMinimalHeader) {
      headerLevel = '2';
    } else if (onReviewPage) {
      headerLevel = `${offsetLevel(2)}`;
    } else {
      headerLevel = `${offsetLevel(1)}`;
    }
  }

  // Helper function to render email addresses consistently
  const renderEmail = emailData => {
    if (!emailData) return '';
    return typeof emailData === 'object'
      ? emailData.emailAddress || ''
      : emailData || '';
  };

  // Render alerts above contact sections
  const renderContactAlerts = () => {
    // Don't show success alerts if there are errors
    if (submitted && (missingInfo.length > 0 || validationErrors.length > 0)) {
      return null;
    }

    const alerts = [];

    Object.entries(fieldConfig).forEach(([id, { text, key }]) => {
      if (!key) return; // Skip if this field is not configured

      const isUpdated = editState === `${id},updated`;

      if (isUpdated) {
        alerts.push(
          <va-alert
            key={`success-${id}`}
            id={`updated-${id}`}
            class="vads-u-margin-y--1"
            status="success"
            role="alert"
          >
            <h2 slot="headline">
              {`We've updated your ${
                text ? text.charAt(0).toLowerCase() + text.slice(1) : ''
              }`}
            </h2>
            <p className="vads-u-margin-y--0">
              {hasProfileUpdate
                ? "We've made these changes to this form and your profile."
                : "We've made these changes to only this form."}
            </p>
          </va-alert>,
        );
      }
    });

    return alerts.length > 0 ? (
      <div className="vads-u-margin-y--3">{alerts}</div>
    ) : null;
  };

  // Return true if a required field is among the missing info fields
  const hasMissingRequiredInfo = key => {
    // get config for a field
    const config = Object.values(fieldConfig).find(
      field => field.fieldName === key,
    );
    // check if a field in missing info matches a field's config
    return missingInfo.some(field =>
      config.text.toLowerCase().startsWith(field),
    );
  };

  // Return true if a field object is missing relevant properties
  // Different from the above function, in that it checks if the field is empty
  // even if not required
  const isContactFieldEmpty = (contactField, contactFieldName) => {
    // If no data object exists, it's empty
    if (!contactField || typeof contactField !== 'object') {
      return true;
    }

    if (contactFieldName === FIELD_NAMES.MAILING_ADDRESS) {
      const { addressLine1, city, countryName, stateCode, zipCode } =
        contactField;
      // Return true if ALL required address fields are falsy
      return (
        isFieldEmpty(addressLine1, contactFieldName) &&
        isFieldEmpty(city, contactFieldName) &&
        isFieldEmpty(countryName, contactFieldName) &&
        isFieldEmpty(stateCode, contactFieldName) &&
        isFieldEmpty(zipCode, contactFieldName)
      );
    }

    if (contactFieldName === FIELD_NAMES.EMAIL) {
      return isFieldEmpty(contactField.emailAddress, contactFieldName);
    }

    if (
      contactFieldName === FIELD_NAMES.HOME_PHONE ||
      contactFieldName === FIELD_NAMES.MOBILE_PHONE
    ) {
      const { areaCode, phoneNumber } = contactField;
      // Return true if ALL phone fields are falsy
      return (
        isFieldEmpty(areaCode, contactFieldName) &&
        isFieldEmpty(phoneNumber, contactFieldName)
      );
    }

    // Default: if we don't recognize the field, consider it empty
    return true;
  };

  // attach a ref that can be used to focus a card that has missing info
  // only attach ref if ref not applied to a preceeding card
  const setErrorCardRef = (hasMissingInfo, ref) => {
    if (hasMissingInfo && errorCardRef.current === null) {
      errorCardRef.current = ref;
    }
  };

  // Extract contact section rendering
  const renderAddressSection = () => {
    if (!keys.address) return null;
    const missingRequiredAddress = hasMissingRequiredInfo(
      FIELD_NAMES.MAILING_ADDRESS,
    );
    setErrorCardRef(missingRequiredAddress, addressErrorCardRef);
    const isAddressEmpty = isContactFieldEmpty(
      dataWrap[keys.address],
      FIELD_NAMES.MAILING_ADDRESS,
    );
    const linkText = isAddressEmpty
      ? content.addMailingAddress
      : content.editMailingAddress;
    const cardContent = isAddressEmpty ? (
      <p className="vads-u-margin-top--0 vads-u-margin-bottom--0">
        Not provided
      </p>
    ) : (
      <AddressView data={dataWrap[keys.address]} />
    );

    return (
      <div ref={addressErrorCardRef}>
        <ContactInfoCard
          key={FIELD_NAMES.MAILING_ADDRESS}
          error={
            showCardErrors && missingRequiredAddress
              ? 'You must add your mailing address'
              : ''
          }
          contactPath={contactPath}
          required={isCardRequired(FIELD_NAMES.MAILING_ADDRESS, keys.address)}
          formKey={keys.address}
          wrapper={keys.wrapper}
          editPath={`${baseEditPath}/edit-mailing-address`}
          headerLevel={headerLevel}
          headerText={content.mailingAddress}
          tagText={missingRequiredAddress ? MISSING_ALERT_TEXT : ''}
          tagStatus={missingRequiredAddress ? 'error' : 'info'}
          linkText={linkText}
        >
          {cardContent}
        </ContactInfoCard>
      </div>
    );
  };

  const renderHomePhoneSection = () => {
    if (!keys.homePhone) return null;
    const missingRequiredHomePhone = hasMissingRequiredInfo(
      FIELD_NAMES.HOME_PHONE,
    );
    setErrorCardRef(missingRequiredHomePhone, homePhoneErrorCardRef);
    const isHomePhoneEmpty = isContactFieldEmpty(
      dataWrap[keys.homePhone],
      FIELD_NAMES.HOME_PHONE,
    );
    const linkText = isHomePhoneEmpty
      ? content.addHomePhone
      : content.editHomePhone;
    const cardContent = isHomePhoneEmpty ? (
      <p className="vads-u-margin-top--0 vads-u-margin-bottom--0">
        Not provided
      </p>
    ) : (
      <div className="dd-privacy-hidden" data-dd-action-name="home phone">
        {renderTelephone(dataWrap[keys.homePhone])}
      </div>
    );
    return (
      <div ref={homePhoneErrorCardRef}>
        <ContactInfoCard
          key={FIELD_NAMES.HOME_PHONE}
          error={
            showCardErrors && missingRequiredHomePhone
              ? 'You must add your home phone number'
              : ''
          }
          contactPath={contactPath}
          required={isCardRequired(FIELD_NAMES.HOME_PHONE, keys.homePhone)}
          formKey={keys.homePhone}
          wrapper={keys.wrapper}
          editPath={`${baseEditPath}/edit-home-phone`}
          headerLevel={headerLevel}
          headerText={content.homePhone}
          tagText={missingRequiredHomePhone ? MISSING_ALERT_TEXT : ''}
          tagStatus={missingRequiredHomePhone ? 'error' : 'info'}
          linkText={linkText}
        >
          {cardContent}
        </ContactInfoCard>
      </div>
    );
  };

  const renderMobilePhoneSection = () => {
    if (!keys.mobilePhone) return null;
    const missingRequiredMobilePhone = hasMissingRequiredInfo(
      FIELD_NAMES.MOBILE_PHONE,
    );
    setErrorCardRef(missingRequiredMobilePhone, mobilePhoneErrorCardRef);
    const isMobilePhoneEmpty = isContactFieldEmpty(
      dataWrap[keys.mobilePhone],
      FIELD_NAMES.MOBILE_PHONE,
    );
    const linkText = isMobilePhoneEmpty
      ? content.addMobilePhone
      : content.editMobilePhone;
    const cardContent = isMobilePhoneEmpty ? (
      <p className="vads-u-margin-top--0 vads-u-margin-bottom--0">
        Not provided
      </p>
    ) : (
      <div className="dd-privacy-hidden" data-dd-action-name="mobile phone">
        {renderTelephone(dataWrap[keys.mobilePhone])}
      </div>
    );

    return (
      <div ref={mobilePhoneErrorCardRef}>
        <ContactInfoCard
          key={FIELD_NAMES.MOBILE_PHONE}
          error={
            showCardErrors && missingRequiredMobilePhone
              ? 'You must add your mobile phone number'
              : ''
          }
          contactPath={contactPath}
          required={isCardRequired(FIELD_NAMES.MOBILE_PHONE, keys.mobilePhone)}
          formKey={keys.mobilePhone}
          wrapper={keys.wrapper}
          editPath={`${baseEditPath}/edit-mobile-phone`}
          headerLevel={headerLevel}
          headerText={content.mobilePhone}
          tagText={missingRequiredMobilePhone ? MISSING_ALERT_TEXT : ''}
          tagStatus={missingRequiredMobilePhone ? 'error' : 'info'}
          linkText={linkText}
        >
          {cardContent}
        </ContactInfoCard>
      </div>
    );
  };

  const renderEmailSection = () => {
    if (!keys.email) return null;
    const missingRequiredEmail = hasMissingRequiredInfo(FIELD_NAMES.EMAIL);
    setErrorCardRef(missingRequiredEmail, emailErrorCardRef);
    const isEmailEmpty = isContactFieldEmpty(
      dataWrap[keys.email],
      FIELD_NAMES.EMAIL,
    );
    const linkText = isEmailEmpty ? content.addEmail : content.editEmail;
    const cardContent = isEmailEmpty ? (
      <p className="vads-u-margin-top--0 vads-u-margin-bottom--0">
        Not provided
      </p>
    ) : (
      <div
        className="dd-privacy-hidden"
        data-dd-action-name="email"
        style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
      >
        {renderEmail(dataWrap[keys.email])}
      </div>
    );

    return (
      <div ref={emailErrorCardRef}>
        <ContactInfoCard
          key={FIELD_NAMES.EMAIL}
          error={
            showCardErrors && missingRequiredEmail
              ? 'You must add your email address'
              : ''
          }
          contactPath={contactPath}
          required={isCardRequired(FIELD_NAMES.EMAIL, keys.email)}
          formKey={keys.email}
          wrapper={keys.wrapper}
          editPath={`${baseEditPath}/edit-email-address`}
          headerLevel={headerLevel}
          headerText={content.email}
          tagText={missingRequiredEmail ? MISSING_ALERT_TEXT : ''}
          tagStatus={missingRequiredEmail ? 'error' : 'info'}
          linkText={linkText}
        >
          {cardContent}
        </ContactInfoCard>
      </div>
    );
  };

  const renderValidationMessages = () => {
    return (
      <div ref={wrapRef}>
        {hadError &&
          missingInfo.length === 0 &&
          validationErrors.length === 0 && (
            <div className="vads-u-margin-top--1p5">
              <va-alert status="success" slim>
                <div className="vads-u-font-size--base">
                  {content.alertContent}
                </div>
              </va-alert>
            </div>
          )}
        {submitted &&
          missingInfo.length === 0 &&
          validationErrors.length > 0 && (
            <div className="vads-u-margin-top--1p5" role="alert">
              <va-alert status="error">
                <div className="vads-u-font-size--base">
                  {validationErrors[0]}
                </div>
              </va-alert>
            </div>
          )}
      </div>
    );
  };

  // Prefill contact edits happen on dedicated edit sub-pages, not inline in the
  // review accordion, so do not show the unused "Update page" button here.
  const navButtons = onReviewPage ? null : (
    <>
      {contentBeforeButtons}
      <FormNavButtons
        goBack={isMinimalHeader ? null : handlers.onGoBack}
        goForward={handlers.onGoForward}
        useWebComponents={useWebComponentNavButtons}
        enableAnalytics={isNavButtonAnalyticsEnabled(formOptions)}
      />
      {contentAfterButtons}
    </>
  );

  const alertsBlock = (
    <>
      {renderValidationMessages()}
      {renderContactAlerts()}
    </>
  );

  return (
    <div className="vads-u-margin-y--2">
      <Element name={`${contactInfoPageKey}ScrollElement`} />
      <form onSubmit={handlers.onSubmit}>
        {addressUpdatedToMatchProfile && (
          <ProfileAddressUpdatedAlert
            appType={
              // same wording channel as the rest of the page: consumers set
              // it via getContent(appName), not formConfig.customText
              content?.appType ||
              router?.routes?.[1]?.formConfig?.customText?.appType
            }
          />
        )}
        {!isMinimalHeader && alertsBlock}
        <MainHeader
          id={`${contactInfoPageKey}Header`}
          className={mainHeaderClass}
        >
          {contactInfoPageTitle ||
            'Confirm the contact information we have on file for you'}
        </MainHeader>
        {contactInfoPageDescription}
        {!loggedIn && (
          <strong className="usa-input-error-message">
            You must be logged in to enable view and edit this page.
          </strong>
        )}
        {isMinimalHeader && alertsBlock}
        <div className="vads-u-margin-top--3">
          <div
            className="va-profile-wrapper vads-l-grid-container vads-u-padding-x--0"
            onSubmit={handlers.onSubmit}
          >
            <div className="vads-l-row">
              <ul
                className="vads-l-col--12 medium-screen:vads-l-col--8 vads-u-padding-left--0 vads-u-margin-top--0"
                style={{ maxWidth: '300px', listStyle: 'none' }}
              >
                {(() => {
                  const sectionMap = {
                    // map keys to rendering functions
                    mailingAddress: renderAddressSection,
                    email: renderEmailSection,
                    homePhone: renderHomePhoneSection,
                    mobilePhone: renderMobilePhoneSection,
                  };
                  const defaultOrder = [
                    renderAddressSection,
                    renderEmailSection,
                    renderHomePhoneSection,
                    renderMobilePhoneSection,
                  ];
                  const orderedSections = order
                    ? order.map(key => sectionMap[key]).filter(Boolean)
                    : defaultOrder;
                  return orderedSections.map((func, i, arr) => {
                    const render = func();
                    // Don't render an empty list item
                    return render ? (
                      <li
                        key={func.name}
                        className={`vads-u-margin-bottom--${
                          i === arr.length - 1 ? '5' : '3'
                        }`}
                      >
                        {render}
                      </li>
                    ) : null;
                  });
                })()}
              </ul>
            </div>
          </div>
        </div>
      </form>
      <div className="vads-u-margin-top--4">{navButtons}</div>
    </div>
  );
};

ContactInfoBase.propTypes = {
  chapterKey: PropTypes.string,
  contactInfoPageDescription: PropTypes.node,
  contactInfoPageKey: contactInfoPropTypes.contactInfoPageKey,
  contactInfoPageTitle: PropTypes.node,
  contactPath: PropTypes.string,
  contactSectionHeadingLevel: PropTypes.string,
  content: contactInfoPropTypes.content, // content passed in from profileContactInfoPage
  contentAfterButtons: PropTypes.element,
  contentBeforeButtons: PropTypes.element,
  data: contactInfoPropTypes.data,
  disableMockContactInfo: PropTypes.bool,
  formContext: PropTypes.shape({
    flatReview: PropTypes.bool,
  }),
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  immediateRedirect: PropTypes.bool,
  keys: contactInfoPropTypes.keys,
  mainHeaderLevel: PropTypes.string,
  order: PropTypes.arrayOf(PropTypes.string),
  prefillPatternEnabled: PropTypes.bool,
  requiredKeys: PropTypes.arrayOf(PropTypes.string),
  schema: PropTypes.shape({
    properties: PropTypes.object,
  }),
  setFormData: PropTypes.func,
  showReviewPageRequiredFields: PropTypes.bool,
  testContinueAlert: PropTypes.bool,
  // for unit testing only
  uiSchema: PropTypes.shape({
    'ui:required': PropTypes.func,
    'ui:validations': PropTypes.array,
  }),
  usePageSchemaForRequiredFields: PropTypes.bool,
  useWebComponentNavButtons: PropTypes.bool,
  onReviewPage: PropTypes.bool,
};

const ContactInfo = withRouter(ContactInfoBase);

export default ContactInfo;
